import { StandardEvents } from '@shopify/events';

/**
 * Bodysocks: makes the collection hero's category chips behave like facets.
 *
 * Each chip is a real link to a filtered collection URL, so it still works
 * without JS and is crawlable. When the collection's facets form is on the page
 * we hand it the URL instead, which re-renders the product grid in place — the
 * same code path the filter checkboxes use, so no full page load.
 *
 * Chip URLs are rebuilt from `location.search` at click time rather than reused
 * from the href, so a chip picked up after the shopper used the filter bar still
 * carries their other filters.
 */
class CollectionChipsComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.collectionUpdate, this.#syncFromLocation);
    window.addEventListener('popstate', this.#syncFromLocation);
    this.#syncFromLocation();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.collectionUpdate, this.#syncFromLocation);
    window.removeEventListener('popstate', this.#syncFromLocation);
  }

  /**
   * The collection's facets form, but only once its custom element has upgraded
   * — facets.js loads at low priority, so an early click must fall back to
   * ordinary link navigation.
   * @returns {(Element & { updateFiltersByURL: (url: string) => void }) | null}
   */
  get #facetsForm() {
    const form = document.querySelector('facets-form-component');
    return form && 'updateFiltersByURL' in form && typeof form.updateFiltersByURL === 'function'
      ? /** @type {any} */ (form)
      : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = /** @type {Element | null} */ (event.target);
    const chip = target?.closest('[data-chip-values]');
    if (!(chip instanceof HTMLElement) || !this.contains(chip)) return;

    const form = this.#facetsForm;
    if (!form) return;

    event.preventDefault();
    form.updateFiltersByURL(this.#urlFor(chip));
    // The facets form only re-renders the product grid section, not this one, so
    // the chips have to move their own highlight.
    this.#syncFromLocation();
  };

  /**
   * Builds the URL a chip should apply: the path its link already points at
   * (which is the plain collection, so a tag-scoped URL is left behind exactly
   * as the un-scripted link would), carrying the filters currently in the URL.
   * @param {HTMLElement} chip
   * @returns {string}
   */
  #urlFor(chip) {
    const url = new URL(chip.getAttribute('href') ?? '', window.location.href);
    const params = new URLSearchParams(window.location.search);
    const param = chip.dataset.chipParam;

    params.delete('page');

    if (!param) {
      // The "shop all" chip clears every facet but keeps the chosen sort order.
      for (const key of [...params.keys()]) {
        if (key.startsWith('filter.')) params.delete(key);
      }
    } else {
      const wasActive = this.#isActive(chip, params);
      params.delete(param);
      if (!wasActive) {
        for (const value of this.#valuesOf(chip)) params.append(param, value);
      }
    }

    url.search = params.toString();
    return url.toString();
  }

  /**
   * @param {HTMLElement} chip
   * @returns {string[]}
   */
  #valuesOf(chip) {
    try {
      const values = JSON.parse(chip.dataset.chipValues ?? '[]');
      return Array.isArray(values) ? values : [];
    } catch {
      return [];
    }
  }

  /**
   * A facet chip is on when the URL holds exactly its values for its param; the
   * "shop all" chip is on when nothing is filtered.
   * @param {HTMLElement} chip
   * @param {URLSearchParams} params
   * @returns {boolean}
   */
  #isActive(chip, params) {
    const param = chip.dataset.chipParam;
    if (!param) return ![...params.keys()].some((key) => key.startsWith('filter.'));

    const values = this.#valuesOf(chip);
    const current = params.getAll(param);
    return current.length === values.length && values.every((value) => current.includes(value));
  }

  #syncFromLocation = () => {
    const params = new URLSearchParams(window.location.search);

    for (const chip of this.querySelectorAll('[data-chip-values]')) {
      if (!(chip instanceof HTMLElement)) continue;
      const active = this.#isActive(chip, params);
      chip.classList.toggle('is-current', active);
      if (active) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
    }

    // Chips that lead to another collection hand the shopper's filters over.
    // Their server-rendered hrefs go stale as soon as filtering happens in
    // place, because only the product grid section re-renders — not this one.
    const carried = new URLSearchParams();
    for (const [key, value] of params) {
      if (key.startsWith('filter.') || key === 'sort_by') carried.append(key, value);
    }
    const query = carried.toString();

    for (const link of this.querySelectorAll('[data-chip-link]')) {
      if (!(link instanceof HTMLElement)) continue;
      const base = link.dataset.chipLink;
      if (!base) continue;
      link.setAttribute('href', query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base);
    }
  };
}

if (!customElements.get('collection-chips-component')) {
  customElements.define('collection-chips-component', CollectionChipsComponent);
}
